/**
 * Runtime parser + shared type for `organizations/{orgId}/payments/{paymentId}`
 * documents written by the Razorpay webhook
 * (`functions/src/billing/razorpay/webhook.ts::buildPaymentDoc`).
 *
 * Replaces the previous blind `data as PaymentRow` cast — which
 * would happily render `NaN` into the money column if the webhook
 * ever wrote a malformed doc. The billing surface is a real-money
 * screen, so we defend against shape drift.
 *
 * Returns `null` on malformed docs (logged via `console.warn`) so
 * the caller can `.filter(Boolean)` them out of the table.
 */

import type { PaidTier } from '@/lib/billingPlans';

export type PaymentRow = {
  /** Firestore doc id (same as `paymentId` for subscription
   *  payments — webhook uses the Razorpay payment id as doc id). */
  id: string;
  /** Razorpay payment id (`pay_xxx`). Always equals `id` for
   *  webhook-written docs but kept separately for clarity. */
  paymentId: string;
  /** Amount in paise (NOT rupees). Divide by 100 for display. */
  amountPaise: number;
  /** Always 'INR' from the Indian Razorpay account. */
  currency: 'INR';
  /** Razorpay payment status. `captured` is the only success
   *  state; everything else is failure / interim. */
  status: 'captured' | 'failed' | 'authorized' | string;
  /** The plan tier this payment was for. May be missing on legacy
   *  docs written before the tier was stamped. */
  tier?: PaidTier;
  /** Billing period. Currently always 'monthly'. */
  period?: 'monthly';
  /** Razorpay payment-capture timestamp (ms). `null` if the doc
   *  arrived without `capturedAt` set — sort it to the end. */
  capturedAtMs: number | null;
  /** Razorpay payment method ('upi' / 'emandate' / etc.). */
  method?: string;
  /** R2 presigned URL to the GST invoice PDF. Stamped by the
   *  webhook in a 2nd write ~5s after capture; may be missing
   *  briefly. */
  invoiceUrl?: string;
  /** Razorpay subscription id this payment belongs to (`sub_xxx`).
   *  `null` for one-time orders. */
  subscriptionId?: string | null;
  /** Razorpay invoice id (`inv_xxx`). Present on subscription
   *  charges. */
  invoiceId?: string | null;
  /** Set by `adminRefundRazorpayPayment` callable when the payment
   *  is refunded. */
  refund?: {
    amountPaise: number;
    refundedAtMs: number | null;
    reason?: string;
  };
};

/** Type guard for a Firestore Timestamp-like value. The web SDK's
 *  `Timestamp` has `toMillis()`; a server-side serialized value
 *  also has `seconds + nanoseconds`. We only accept the
 *  `toMillis()` shape here. */
function toMillisOrNull(v: unknown): number | null {
  if (
    v &&
    typeof v === 'object' &&
    typeof (v as { toMillis?: unknown }).toMillis === 'function'
  ) {
    try {
      const ms = (v as { toMillis: () => number }).toMillis();
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
    } catch {
      return null;
    }
  }
  return null;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Parse + validate a Firestore payment doc. Returns null and
 *  logs a warning on shape drift; never throws. */
export function parsePaymentDoc(id: string, data: unknown): PaymentRow | null {
  if (!isObject(data)) {
    console.warn('[parsePaymentDoc] not an object', { id });
    return null;
  }

  const amount = data.amount;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) {
    console.warn('[parsePaymentDoc] invalid amount', { id, amount });
    return null;
  }

  const currency = data.currency;
  if (currency !== 'INR') {
    console.warn('[parsePaymentDoc] unexpected currency', { id, currency });
    return null;
  }

  const status =
    typeof data.status === 'string' && data.status.length > 0
      ? data.status
      : 'unknown';

  const paymentId =
    typeof data.paymentId === 'string' && data.paymentId.length > 0
      ? data.paymentId
      : id;

  const tier =
    data.tier === 'solo' ||
    data.tier === 'studio' ||
    data.tier === 'studio_plus' ||
    data.tier === 'agency'
      ? (data.tier as PaidTier)
      : undefined;

  const period = data.period === 'monthly' ? 'monthly' : undefined;

  const capturedAtMs = toMillisOrNull(data.capturedAt);

  const invoiceUrl =
    typeof data.invoiceUrl === 'string' && data.invoiceUrl.length > 0
      ? data.invoiceUrl
      : undefined;

  const method = typeof data.method === 'string' ? data.method : undefined;

  const subscriptionId =
    typeof data.subscriptionId === 'string' ? data.subscriptionId : null;
  const invoiceId =
    typeof data.invoiceId === 'string' ? data.invoiceId : null;

  let refund: PaymentRow['refund'] | undefined;
  if (isObject(data.refund)) {
    const ra = data.refund.amount;
    if (typeof ra === 'number' && Number.isFinite(ra) && ra > 0) {
      refund = {
        amountPaise: ra,
        refundedAtMs: toMillisOrNull(data.refund.refundedAt),
        reason:
          typeof data.refund.reason === 'string'
            ? data.refund.reason
            : undefined,
      };
    }
  }

  return {
    id,
    paymentId,
    amountPaise: amount,
    currency: 'INR',
    status,
    tier,
    period,
    capturedAtMs,
    method,
    invoiceUrl,
    subscriptionId,
    invoiceId,
    refund,
  };
}

/** Convenience: paise → rupees, rounded to 2dp safely. */
export function paiseToInr(paise: number): number {
  return Math.round(paise) / 100;
}

/** "₹599" / "₹1,99,900" — Indian-locale grouped. */
export function formatInr(rupees: number): string {
  return `₹${rupees.toLocaleString('en-IN')}`;
}

/** "₹2.2K" / "₹1.99L" / "₹2.5Cr" — compact for stat tiles. */
export function formatInrCompact(rupees: number): string {
  if (!rupees) return '₹0';
  const abs = Math.abs(rupees);
  if (abs >= 1_00_00_000) return `₹${(rupees / 1_00_00_000).toFixed(2)}Cr`;
  if (abs >= 1_00_000) return `₹${(rupees / 1_00_000).toFixed(2)}L`;
  if (abs >= 1000) return `₹${(rupees / 1000).toFixed(1)}K`;
  return `₹${rupees.toLocaleString('en-IN')}`;
}
